import { Form, Formik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import { number, object } from "yup";
// @ts-expect-error pure js
import TextInput from "../UI/TextInput";
import styles from "./AuthForm.module.css";
// @ts-expect-error pure js
import axios from "../../api";
import { useState, type ReactNode } from "react";
import Callout from "../UI/Callout";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import { trackMetaEvent } from "../../utils/metaPixel";
import { trackEvent } from "../../utils/tracking";
// @ts-expect-error pure js
import useAuth from "../../hooks/use-auth.js";
import AuthHeader from "./AuthHeader";
import AuthFooter from "./AuthFooter";
import FormikSubmitButton from "../UI/FormikSubmitButton.tsx";
import Button from "../UI/Button";
import Text from "../UI/Text";

interface VerifyValues {
  hash: string;
}

const INITIAL_VALUES: VerifyValues = { hash: "" };
const VALIDATION_SCHEMA = object({
  hash: number()
    .integer()
    .positive()
    .required("Code invalide")
    .typeError("Code invalide"),
});

const RESEND_COOLDOWN_MS = 30_000;

function extractErrorMessage(err: unknown): ReactNode {
  const axiosError = err as { response?: { data?: { error?: string } } };
  return axiosError.response?.data?.error ?? handleAPIError(err);
}

export default function AuthFormSignupVerify() {
  const { buildAndSetAuth } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const state = location.state as { mail?: string } | null;
  // Le lien du mail de confirmation porte l'adresse en paramètre d'URL ; le suivi de pages
  // la déplace aussitôt dans le state de navigation.
  const mail = state?.mail ?? new URLSearchParams(location.search).get("mail");
  const [error, setError] = useState<ReactNode>("");
  const [info, setInfo] = useState("");
  const [resendDisabled, setResendDisabled] = useState(false);

  async function signMeUp(values: VerifyValues) {
    try {
      setError("");
      const data = {
        hash: values.hash,
        mail,
      };
      const response = await axios.post("/signup/verify", data);
      buildAndSetAuth(response.data);
      trackMetaEvent("CompleteRegistration", `reg_${response.data.id_compte}`);
      trackEvent("CompleteRegistration", location, {
        event_id: `reg_${response.data.id_compte}`,
        user_data: { email: response.data.mail ?? mail },
      });
      nav(response.data.home);
    } catch (err: unknown) {
      setError(extractErrorMessage(err));
    }
  }

  async function resendCode() {
    try {
      setError("");
      setInfo("");
      await axios.post("/signup/resend-code", { mail });
      setInfo(
        "Si un compte est en attente de vérification pour cette adresse, un nouvel email contenant votre code vient de vous être envoyé. Pensez à vérifier vos courriers indésirables.",
      );
      setResendDisabled(true);
      setTimeout(() => setResendDisabled(false), RESEND_COOLDOWN_MS);
    } catch (err: unknown) {
      setError(extractErrorMessage(err));
    }
  }

  return (
    <Formik
      initialValues={INITIAL_VALUES}
      validationSchema={VALIDATION_SCHEMA}
      onSubmit={signMeUp}
    >
      {() => (
        <Form className={styles.form}>
          <AuthHeader title="Confirmer l'adresse e-mail" />

          <TextInput
            type="text"
            name="hash"
            label="Code de confirmation"
            placeholder="Entrez le code reçu par mail"
            autoComplete="one-time-code"
            inputMode="numeric"
            marginForm=""
          />

          <FormikSubmitButton className={styles.submitButton}>
            Valider
          </FormikSubmitButton>

          {mail && (
            <div className={styles.resend}>
              <Text variant="deci" color="muted" as="span">
                Vous n'avez rien reçu ?
              </Text>
              <Button
                type="button"
                variant="tertiary"
                size="sm"
                disabled={resendDisabled}
                onClick={resendCode}
              >
                Renvoyer le code
              </Button>
            </div>
          )}

          <Callout variant="success">{info}</Callout>

          <Callout variant="danger">{error}</Callout>

          <AuthFooter
            label="Déjà un compte ?"
            linkText="Se connecter"
            to="/login"
          />
        </Form>
      )}
    </Formik>
  );
}
